<template>
  <b-overlay :show="service.status && !['success', 'error'].includes(service.status)" variant="white">
    <b-container fluid class="px-0">
      <b-row class="row-nowrap mx-0" align-v="center">
        <b-col cols="7">
          <b-row class="row-nowrap py-1" cols="2"
            v-b-tooltip.hover.left.d300 :title="$t('PID: {pid}', service)"
          >
            <b-col cols="6" class="text-nowrap">{{ $i18n.t('Alive') }}</b-col>
            <b-col cols="auto" class="text-right ml-auto"><icon :class="(service.alive && service.pid) ? 'text-success' : 'text-danger'" name="circle"/></b-col>
          </b-row>
        </b-col>
        <b-col cols="5" class="text-wrap" v-if="isAllowed">
          <b-button v-if="restart && service.alive && service.pid"
            @click="doRestart(server)" :disabled="isLoading" variant="link" size="sm" class="text-nowrap text-secondary mr-1">
            <icon name="redo" class="mr-1" /> {{ $i18n.t('Restart') }}
          </b-button>
          <b-button v-if="start && !(service.alive && service.pid)"
            @click="doStart(server)" :disabled="isLoading" variant="link" size="sm" class="text-nowrap text-secondary mr-1">
            <icon name="play" class="mr-1" /> {{ $i18n.t('Start') }}
          </b-button>
          <b-button v-if="stop && service.alive && service.pid"
            @click="doStop(server)" :disabled="isLoading" variant="link" size="sm" class="text-nowrap text-secondary mr-1">
            <icon name="stop" class="mr-1" /> {{ $i18n.t('Stop') }}
          </b-button>
        </b-col>
      </b-row>
      <b-row v-if="service.message"
        class="mt-2 mx-0">
        <b-col cols="auto" class="small text-danger text-wrap">
          <icon name="info-circle" scale="1.5" class="mr-1" /> {{ service.message }}
        </b-col>
      </b-row>
    </b-container>
    <template v-slot:overlay v-if="service.status && service.status !== 'loading'">
      <b-row class="justify-content-md-center">
        <b-col cols="auto">
          <b-media class="text-gray text-uppercase font-weight-bold">
            <template v-slot:aside><icon name="circle-notch" spin scale="1.5" /></template>
            <p v-if="service.status === 'restarting'" class="mb-0">{{ $i18n.t('Restarting') }}</p>
            <p v-if="service.status === 'starting'" class="mb-0">{{ $i18n.t('Starting') }}</p>
            <p v-if="service.status === 'stopping'" class="mb-0">{{ $i18n.t('Stopping') }}</p>
          </b-media>
        </b-col>
      </b-row>
    </template>
  </b-overlay>
</template>
<script>
const props = {
  id: {
    type: String
  },
  server: {
    type: String
  },
  restart: {
    type: Boolean
  },
  start: {
    type: Boolean
  },
  stop: {
    type: Boolean
  },
  acl: {
    type: String,
    default: 'SERVICES_READ'
  },
  lazy: {
    type: Boolean
  }
}

import { computed, toRefs, watch } from '@vue/composition-api'
import acl from '@/utils/acl'
import i18n from '@/utils/locale'
import { localeStrings } from '@/globals/pfLocales'

const setup = (props, context) => {

  const {
    id,
    server,
    lazy,
    acl: _acl
  } = toRefs(props)

  const { emit, root: { $store } = {} } = context

  const isAllowed = computed(() => {
    if (_acl.value) {
      const [ verb, ...nouns ] = Array.prototype.slice.call(_acl.value.toLowerCase().split('_')).reverse()
      const noun = nouns.reverse().join('_')
      return verb && nouns.length > 0 && acl.$can(verb, noun)
    }
    return true
  })

  watch([id, server], () => {
    if (id.value && server.value && isAllowed.value && !lazy.value) {
      $store.dispatch('system/getHostname').then(() => {
        $store.dispatch('cluster/getSystemService', { server: server.value, id: id.value })
      })
    }
  }, { immediate: true })
  const service = computed(() => $store.state.cluster.servers[server.value].system_services[id.value] || {})
  const isCluster = computed(() => $store.getters['cluster/isCluster'])
  const isLoading = computed(() => $store.getters['cluster/isLoading'])

  const doRestart = () => $store.dispatch('cluster/restartSystemService', { server: server.value, id: id.value }).then(() => {
    $store.dispatch('notification/info', { url: server.value, message: i18n.t(localeStrings.SERVICES_RESTARTED_SUCCESS, { services: `<code>${id.value}</code>` }) })
    emit('restart', { server, id: id.value })
  }).catch(() => {
    const message = i18n.t(localeStrings.SERVICES_RESTARTED_ERROR, { services: `<code>${id.value}</code>` })
    $store.dispatch('notification/danger', { url: server.value, message })
  })

  const doStart = () => $store.dispatch('cluster/startSystemService', { server: server.value, id: id.value }).then(() => {
    $store.dispatch('notification/info', { url: server.value, message: i18n.t(localeStrings.SERVICES_STARTED_SUCCESS, { services: `<code>${id.value}</code>` }) })
    emit('start', { server, id: id.value })
  }).catch(() => {
    $store.dispatch('notification/danger', { url: server.value, message: i18n.t(localeStrings.SERVICES_STARTED_ERROR, { services: `<code>${id.value}</code>` }) })
  })

  const doStop = () => $store.dispatch('cluster/stopSystemService', { server: server.value, id: id.value }).then(() => {
    $store.dispatch('notification/info', { url: server.value, message: i18n.t(localeStrings.SERVICES_STOPPED_SUCCESS, { services: `<code>${id.value}</code>` }) })
    emit('stop', { server, id: id.value })
  }).catch(() => {
    $store.dispatch('notification/danger', { url: server.value, message: i18n.t(localeStrings.SERVICES_STOPPED_ERROR, { services: `<code>${id.value}</code>` }) })
  })

  return {
    service,

    isAllowed,
    isCluster,
    isLoading,

    doRestart,
    doStart,
    doStop,
  }
}

// @vue/component
export default {
  name: 'base-service-system',
  props,
  setup
}
</script>
